'use client'

import { useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAccount, useConfig } from 'wagmi'
import { simulateEVCBatchCall, type TransactionItems, type ExtendedConfig } from '@eulerxyz/wagmi-evc'
import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import type { EulerSwapResponse } from './types'

export interface UseSimulatedBestQuoteParams {
  /** All quotes sorted by preference (best first), from useEulerSwapQuote. */
  quotes: EulerSwapResponse[]
  /**
   * Builds TransactionItems for a given quote.
   * The hook uses a ref internally so the callback identity doesn't affect the query key.
   */
  buildTransactions: (quote: EulerSwapResponse) => TransactionItems
  /**
   * Additional values to include in the query key.
   * Pass anything that affects `buildTransactions` output (e.g. position, vault address).
   */
  queryKeyDeps?: unknown[]
  /** Whether the hook is enabled. Defaults to true. */
  enabled?: boolean
}

export interface UseSimulatedBestQuoteResult {
  /** The first quote that passed simulation (preserving preference order), or null. */
  quote: EulerSwapResponse | null
  /** The TransactionItems for the winning quote (ready for useSimulateEVCBatchCall). */
  transactions: TransactionItems
  /** Whether simulation is in progress. */
  isSimulating: boolean
  /** Error if all quotes failed simulation. */
  error: Error | null
  /** Manually re-run simulation for all quotes. */
  refetch: () => void
}

/**
 * Simulates all provided swap quotes in parallel via `simulateEVCBatchCall` and
 * returns the first one that passes (doesn't revert).
 *
 * Uses `simulateEVCBatchCall` instead of `simulation()` because:
 * - `simulation()` relies on SimulationFactory which fetches from /api/v1/accounts/vaults
 *   (not available in all environments).
 * - `simulateEVCBatchCall` does pure on-chain simulation via eth_call — no external API deps.
 * - If a batch call succeeds without reverting, the quote is valid.
 *
 * Usage:
 * ```ts
 * const { quotes } = useEulerSwapQuote({ ... })
 * const buildTxs = useCallback((quote) => { ... }, [deps])
 *
 * const { quote, transactions, isSimulating, error } = useSimulatedBestQuote({
 *   quotes,
 *   buildTransactions: buildTxs,
 *   queryKeyDeps: [position, vaultAddress],
 *   enabled: quotes.length > 0,
 * })
 *
 * // Pass `transactions` to useSimulateEVCBatchCall for the execution `request`.
 * ```
 */
export function useSimulatedBestQuote({
  quotes,
  buildTransactions,
  queryKeyDeps = [],
  enabled = true,
}: UseSimulatedBestQuoteParams): UseSimulatedBestQuoteResult {
  const config = useConfig()
  const { address } = useAccount()
  const chainId = E_LEND_CHAIN_ID

  // useRef (not useCallback) is intentional here — recommended by React Query docs:
  //
  // - useCallback in queryKey would cause re-fetches whenever deps change, even
  //   when the quotes themselves haven't changed → wasted RPC simulations.
  // - useEffect for ref update would be worse: it runs *after* render, leaving a
  //   brief window where the ref holds the old callback. Synchronous assignment
  //   during render has no such timing gap.
  //
  // No race condition with stale callbacks because:
  // 1. queryKeyDeps includes all values that affect buildTransactions output
  //    (position, vault address, amounts, etc.) — callers are responsible for this.
  // 2. Quote fingerprint in the queryKey invalidates on new API responses.
  // 3. The ref is updated synchronously every render, so by the time queryFn
  //    runs it always has the latest callback.
  const buildTransactionsRef = useRef(buildTransactions)
  buildTransactionsRef.current = buildTransactions

  const query = useQuery({
    queryKey: [
      'simulatedBestQuote',
      // Fingerprint quotes by amounts, routes, and calldata — ensures re-simulation when the
      // API returns different data even if amounts are unchanged.
      // Uses JSON.stringify on swap/verify to capture any calldata or verifier field changes.
      quotes
        .map((q) =>
          JSON.stringify({
            amountIn: q.amountIn,
            amountOut: q.amountOut,
            swap: q.swap,
            verify: q.verify,
          }),
        )
        .join('|'),
      address,
      chainId,
      ...queryKeyDeps,
    ],
    queryFn: async (): Promise<{ quote: EulerSwapResponse; transactions: TransactionItems }> => {
      // Simulate all quotes in parallel (matching euler-monorepo Promise.allSettled pattern).
      // Quote count is inherently bounded by the Euler Swap API response (typically 1-5 quotes),
      // so explicit MAX_QUOTES limiting is unnecessary.
      const results = await Promise.allSettled(
        quotes.map(async (quote) => {
          const transactions = buildTransactionsRef.current(quote)
          if (!transactions || transactions.length === 0) {
            return null
          }

          try {
            // Use simulateEVCBatchCall — pure on-chain simulation, no external API.
            // If the call succeeds (returns a request), the quote is valid.
            await simulateEVCBatchCall(
              config as ExtendedConfig,
              {
                chainId,
                transactions,
              } as any,
            )

            return { quote, transactions }
          } catch (error) {
            console.warn('[useSimulatedBestQuote] Quote failed simulation:', {
              amountIn: quote.amountIn,
              amountOut: quote.amountOut,
              route: quote.route?.map((r: { providerName?: string }) => r.providerName).join(', '),
              error: (error as Error)?.message?.slice(0, 200),
            })
            return null
          }
        }),
      )

      // Pick the first passing quote (preserving preference order from sortQuotesByPreference).
      for (const result of results) {
        if (result.status === 'fulfilled' && result.value) {
          return result.value
        }
      }

      throw new Error('Quote not found')
    },
    enabled: enabled && quotes.length > 0 && !!address,
    staleTime: 10_000,
    gcTime: 30_000,
    retry: 1,
  })

  return {
    quote: query.data?.quote ?? null,
    transactions: query.data?.transactions ?? [],
    isSimulating: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  }
}
